import { interpolateColor, M_PER_DEG_LAT, type ColorStops } from '@/core/utils';
import { MAX_REACHED_COLOR } from '../model/colorScale';
import { isHit, margin } from '../model/result';
import type { ConeResult } from '../model/types';
import type { ConeImage } from './glideStyle';

const ALPHA = 200;

/** RGBA pixels, one per grid cell: result cells coloured by margin, others transparent. */
export function paintCone(r: ConeResult, stops: ColorStops): Uint8ClampedArray<ArrayBuffer> {
  const { size, halfSize: N, cellSize: cs, radius: R } = r;
  const px = new Uint8ClampedArray(size * size * 4);
  const markEdge = r.limited && R / cs >= 2;
  const edge2 = (R / cs - 1.5) ** 2;
  for (let k = 0; k < size * size; k++) {
    if (!isHit(r, k)) continue;
    const x = (k % size) - N;
    const y = ((k / size) | 0) - N;
    const c = markEdge && x * x + y * y > edge2 ? MAX_REACHED_COLOR : interpolateColor(stops, margin(r, k));
    px.set([c[0], c[1], c[2], ALPHA], k * 4);
  }
  return px;
}

/** The result as an image source: a PNG data URL and its corner coordinates. */
export function coneImage(r: ConeResult, stops: ColorStops): ConeImage {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = r.size;
  canvas.getContext('2d')!.putImageData(new ImageData(paintCone(r, stops), r.size, r.size), 0, 0);

  const h = (r.halfSize + 0.5) * r.cellSize;
  const toLngLat = (xm: number, ym: number): [number, number] => [
    r.origin.lng + xm / r.mPerDegLng,
    r.origin.lat + ym / M_PER_DEG_LAT,
  ];
  return {
    url: canvas.toDataURL(),
    coordinates: [toLngLat(-h, h), toLngLat(h, h), toLngLat(h, -h), toLngLat(-h, -h)],
  };
}
